import React, { useEffect, useState } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const REASON_MIN = 20

type View = 'suspended' | 'search' | 'review'

interface AccountRow {
  id: number
  username: string
  email: string
  fullName: string | null
  phone?: string
  accountStatus: string
  role?: string
  createdAt?: string
  lastLoginAt?: string | null
  suspensionReason?: string | null
  suspendedAt?: string | null
  suspendedBy?: string | null
  sponsor?: { id: number; username: string; fullName: string | null } | null
  downlineCount?: number
  sharePurchaseCount?: number
  shareCount?: number
  commissionBalance?: number | string | null
  kyc?: {
    status: string | null
    legalName: string
    dateOfBirth: string | null
    city: string | null
    country: string | null
    idType: string | null
  } | null
}

interface ReviewGroup {
  dateOfBirth: string
  members: Array<{
    id: number
    username: string
    email: string
    fullName: string
    legalName: string
    accountStatus: string
  }>
}

const shell: React.CSSProperties = { color: '#e5e7eb', maxWidth: '1180px' }
const card: React.CSSProperties = { background: '#111827', border: '1px solid #1f2937', borderRadius: '16px', padding: '20px' }
const inputStyle: React.CSSProperties = { background: '#0b1220', color: '#f9fafb', border: '1px solid #374151', borderRadius: '10px', padding: '10px 12px', width: '100%' }
const cell: React.CSSProperties = { padding: '10px 8px', verticalAlign: 'top' }

function buttonStyle(background: string): React.CSSProperties {
  return { background, color: '#fff', border: 0, borderRadius: '10px', padding: '10px 14px', cursor: 'pointer', fontWeight: 600 }
}

function formatWhen(value?: string | null) {
  if (!value) return '—'
  return value.slice(0, 16).replace('T', ' ')
}

const AccountSuspensionDesk: React.FC = () => {
  const [view, setView] = useState<View>('suspended')
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<AccountRow[]>([])
  const [groups, setGroups] = useState<ReviewGroup[]>([])
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState<AccountRow | null>(null)
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [acting, setActing] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [confirmArchive, setConfirmArchive] = useState(false)

  const loadSuspended = async (nextQuery = query) => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ view: 'suspended', q: nextQuery.trim(), limit: '50', offset: '0' })
      const response = await fetch(`${API_BASE_URL}/api/admin/account-suspensions?${params}`, { headers: getSessionHeaders() })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Could not load suspended accounts')
      setRows(body.rows || [])
      setTotal(body.total || 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load suspended accounts')
    } finally {
      setLoading(false)
    }
  }

  const loadReview = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/account-suspensions?view=review`, { headers: getSessionHeaders() })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Could not load the review list')
      setGroups(body.groups || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the review list')
    } finally {
      setLoading(false)
    }
  }

  const openAccount = async (userId: number) => {
    setError('')
    setNotice('')
    setConfirmArchive(false)
    const response = await fetch(`${API_BASE_URL}/api/admin/account-suspensions?view=detail&userId=${userId}`, { headers: getSessionHeaders() })
    const body = await response.json()
    if (!response.ok) {
      setError(body.error || 'Could not load the account')
      return
    }
    setSelected(body.account)
  }

  useEffect(() => {
    setRows([])
    setTotal(0)
    if (view === 'suspended') loadSuspended('')
    if (view === 'review') loadReview()
  }, [view])

  const search = async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ view: 'search', q: query.trim(), limit: '25' })
      const response = await fetch(`${API_BASE_URL}/api/admin/account-suspensions?${params}`, { headers: getSessionHeaders() })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Search failed')
      setRows(body.rows || [])
      setTotal(body.rows?.length || 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  const runAction = async (action: 'suspend' | 'unsuspend' | 'archive') => {
    if (!selected) return
    setActing(true)
    setError('')
    setNotice('')
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/account-suspensions`, {
        method: 'POST',
        headers: { ...getSessionHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, userId: selected.id, reason }),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'The action was refused')
      if (action === 'archive') {
        const moved = Number(body.reparentedDownline || 0)
        setNotice(moved > 0
          ? `${selected.username} was archived. ${moved} direct referral${moved === 1 ? '' : 's'} now sit under their sponsor, who receives the commission this account would have received.`
          : `${selected.username} was archived. The live login was removed and the record is in the removed-account archive.`)
        setSelected(null)
      } else {
        setNotice(action === 'suspend' ? `${selected.username} is suspended.` : `${selected.username} is active again.`)
        await openAccount(selected.id)
      }
      setReason('')
      setConfirmArchive(false)
      if (view === 'suspended') await loadSuspended()
      if (view === 'review') await loadReview()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The action was refused')
    } finally {
      setActing(false)
    }
  }

  const reasonReady = reason.trim().length >= REASON_MIN

  return (
    <div style={shell}>
      <div style={{ ...card, marginBottom: '16px' }}>
        <h2 style={{ margin: '0 0 8px' }}>Account suspensions</h2>
        <p style={{ margin: 0, color: '#9ca3af', lineHeight: 1.5 }}>
          Suspended accounts are listed here with the reason they were stopped. Search finds any account. Duplicate identities are listed for review and are not suspended until you choose that action. Delete archives the login only when the account has no shares, balance, certificate, payment, or commission history. A downline on such an account is moved to its sponsor.
        </p>
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          {([
            ['suspended', 'Suspended'],
            ['search', 'Find an account'],
            ['review', 'Duplicate review'],
          ] as Array<[View, string]>).map(([id, label]) => (
            <button key={id} type="button" onClick={() => { setView(id); setSelected(null); setNotice(''); setError('') }} style={buttonStyle(view === id ? '#1d4ed8' : '#374151')}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {error ? <p style={{ color: '#fca5a5' }}>{error}</p> : null}
      {notice ? <p style={{ color: '#86efac' }}>{notice}</p> : null}

      {view !== 'review' ? (
        <div style={{ ...card, marginBottom: '16px' }}>
          <form onSubmit={(event) => { event.preventDefault(); view === 'search' ? search() : loadSuspended() }} style={{ display: 'flex', gap: '8px' }}>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Username, name, email, or account id" style={inputStyle} />
            <button type="submit" style={buttonStyle('#1d4ed8')}>{view === 'search' ? 'Search' : 'Filter'}</button>
          </form>
          <p style={{ color: '#9ca3af' }}>{loading ? 'Loading…' : `${total} account${total === 1 ? '' : 's'}`}</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#93c5fd' }}>
                  <th style={cell}>Id</th>
                  <th style={cell}>Username</th>
                  <th style={cell}>Name</th>
                  <th style={cell}>Status</th>
                  <th style={cell}>Reason</th>
                  <th style={cell}>Suspended</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id} onClick={() => openAccount(row.id)} style={{ borderTop: '1px solid #1f2937', cursor: 'pointer', background: selected?.id === row.id ? '#172554' : 'transparent' }}>
                    <td style={cell}>{row.id}</td>
                    <td style={cell}>{row.username}</td>
                    <td style={cell}>{row.fullName || '—'}</td>
                    <td style={cell}>{row.accountStatus}</td>
                    <td style={cell}>{row.suspensionReason || '—'}</td>
                    <td style={cell}>{formatWhen(row.suspendedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={{ ...card, marginBottom: '16px' }}>
          <p style={{ color: '#9ca3af' }}>{loading ? 'Comparing identity records…' : `${groups.length} duplicate group${groups.length === 1 ? '' : 's'} for review. Nothing in this list has been suspended by opening it.`}</p>
          {groups.map(group => (
            <div key={`${group.dateOfBirth}-${group.members.map(member => member.id).join('-')}`} style={{ borderTop: '1px solid #1f2937', padding: '12px 0' }}>
              <strong>Birthday {group.dateOfBirth}</strong>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {group.members.map(member => (
                  <button key={member.id} type="button" onClick={() => openAccount(member.id)} style={{ ...buttonStyle(member.accountStatus === 'suspended' ? '#7f1d1d' : '#1f2937'), textAlign: 'left' }}>
                    {member.username} · {member.legalName} · {member.accountStatus}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected ? (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>{selected.username}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            <Detail label="Account id" value={String(selected.id)} />
            <Detail label="Email" value={selected.email} />
            <Detail label="Full name" value={selected.fullName || '—'} />
            <Detail label="Phone" value={selected.phone || '—'} />
            <Detail label="Status" value={selected.accountStatus} />
            <Detail label="Role" value={selected.role || 'user'} />
            <Detail label="Created" value={formatWhen(selected.createdAt)} />
            <Detail label="Last login" value={formatWhen(selected.lastLoginAt)} />
            <Detail label="Suspended at" value={formatWhen(selected.suspendedAt)} />
            <Detail label="Suspended by" value={selected.suspendedBy || '—'} />
            <Detail label="Sponsor" value={selected.sponsor ? `${selected.sponsor.username} (#${selected.sponsor.id})` : 'None'} />
            <Detail label="Direct downline" value={String(selected.downlineCount ?? 0)} />
            <Detail label="Share records" value={String(selected.sharePurchaseCount ?? 0)} />
            <Detail label="Shares" value={String(selected.shareCount ?? 0)} />
            <Detail label="Commission balance" value={selected.commissionBalance == null ? 'None' : String(selected.commissionBalance)} />
            <Detail label="KYC" value={selected.kyc ? `${selected.kyc.status || 'unknown'} · ${selected.kyc.legalName}` : 'No KYC'} />
            <Detail label="KYC birthday" value={selected.kyc?.dateOfBirth || '—'} />
            <Detail label="KYC place" value={selected.kyc ? [selected.kyc.city, selected.kyc.country].filter(Boolean).join(', ') || '—' : '—'} />
            <Detail label="ID type" value={selected.kyc?.idType || '—'} />
          </div>
          <p style={{ color: '#fde68a' }}><strong>Suspension reason:</strong> {selected.suspensionReason || 'None recorded'}</p>
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why is this action being taken?" style={{ ...inputStyle, minHeight: '90px' }} />
          <div style={{ color: reasonReady ? '#86efac' : '#fca5a5', fontSize: '13px', margin: '6px 0 12px' }}>{reason.trim().length} / {REASON_MIN}</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" disabled={!reasonReady || acting || selected.accountStatus === 'suspended'} onClick={() => runAction('suspend')} style={buttonStyle(reasonReady ? '#b45309' : '#4b5563')}>Suspend</button>
            <button type="button" disabled={!reasonReady || acting || selected.accountStatus !== 'suspended'} onClick={() => runAction('unsuspend')} style={buttonStyle(reasonReady ? '#047857' : '#4b5563')}>Unsuspend</button>
            <button type="button" disabled={!reasonReady || acting} onClick={() => setConfirmArchive(true)} style={buttonStyle(reasonReady ? '#991b1b' : '#4b5563')}>Delete</button>
          </div>
          {confirmArchive ? (
            <div style={{ marginTop: '16px', border: '1px solid #7f1d1d', borderRadius: '12px', padding: '14px' }}>
              <p style={{ marginTop: 0 }}>
                Delete copies {selected.username} into the removed-account archive and removes the live login. It is refused when the account has shares, a commission balance, commission history, a certificate, or a payment. If there is a downline and none of those records, the direct referrals move to {selected.sponsor ? selected.sponsor.username : 'the sponsor'}, who then receives the commission this account would have received.
              </p>
              <button type="button" disabled={acting} onClick={() => runAction('archive')} style={buttonStyle('#991b1b')}>Confirm delete</button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: '#9ca3af', fontSize: '12px' }}>{label}</div>
      <div>{value}</div>
    </div>
  )
}

export default AccountSuspensionDesk
